import { Autocomplete, TextField } from "@mui/material";
import { Link } from "react-router-dom";
// import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CancelIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

import { useState } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Style mappings for filled and outline buttons
const variantStyles = {
  add: {
    filled: "text-white bg-blue-600 hover:bg-blue-700",
    outline: "text-blue-600 border border-blue-600 hover:bg-gray-100",
  },
  edit: {
    filled: "text-white bg-green-600 hover:bg-green-700",
    outline: "text-green-700 border border-green-700 hover:bg-gray-100",
  },
  delete: {
    filled: "text-white bg-red-600 hover:bg-red-700",
    outline: "text-red-600 border border-red-600 hover:bg-gray-100",
  },
  search: {
    filled: "text-white bg-blue-600 hover:bg-blue-700",
    outline:
      "text-blue-600 border border-blue-600 hover:bg-blue-50 bg-transparent",
  },
  copy: {
    filled: "text-white bg-purple-600 hover:bg-purple-700",
    outline: "text-purple-600 border border-purple-600 hover:bg-gray-100",
  },
  cancel: {
    filled: "text-white bg-gray-500 hover:bg-gray-600",
    outline: "text-blue-600 border border-blue-600 hover:bg-blue-100",
  },
  upload: {
    filled: "text-white bg-indigo-600 hover:bg-indigo-700",
    outline: "text-indigo-600 border border-indigo-600 hover:bg-gray-100",
  },
  approve: {
    filled: "text-white bg-violet-600 hover:bg-violet-700",
    outline: "text-violet-600 border border-violet-600 hover:bg-gray-100",
  },
};

// Base button
const CreateButton = ({
  to,
  label,
  id,
  icon,
  variant = "add",
  outline = false,
  onClick,
  disabled = false,
  hoverTitle = "",
}) => {
  const baseClasses =
    "btn font-sans text-md font-medium py-2 px-3 rounded inline-flex items-center justify-center gap-2";
  const colorClasses =
    variantStyles[variant]?.[outline ? "outline" : "filled"] ||
    variantStyles["add"].filled;

   // Add conditional styling for disabled state
  const disabledClasses = disabled
    ? "opacity-50 cursor-not-allowed pointer-events-none"
    : "";

  const finalClass = `${baseClasses} ${colorClasses} ${disabledClasses}`;  

  const button = (
    <button id={id} className={finalClass} type="button" onClick={onClick} disabled={disabled} title={hoverTitle}>
      {icon && <span>{icon}</span>}
      {label}
    </button>
  );

  return to ? <Link to={to}>{button}</Link> : button;
};

// Named buttons for direct import
export const AddButton = (props) => <CreateButton variant="add" {...props}  />;

export const SaveAndUpdate = (props) => <CreateButton {...props} variant="add" />;

export const EditButton = (props) => (
  <CreateButton
    {...props}
    variant="edit"
    icon={<EditIcon fontSize="small" />}
  />
);

export const SearchToggleButton = ({
  searched,
  onSearch,
  onClear,
}) => (
  <CreateButton
    id="search-toggle-btn"
    variant={searched ? "delete" : "search"}
    outline={false}
    label={searched ? "Reset" : "Search"}
    icon={
      searched ? (
        <RestartAltIcon fontSize="small" />
      ) : (
        <SearchIcon fontSize="small" />
      )
    }
    onClick={() => {
      searched ? onClear() : onSearch();
    }}
  />
);

export const DeleteButton = (props) => (
  <CreateButton
    {...props}
    variant="delete"
    icon={<DeleteIcon fontSize="small" />}
  />
);

export const SearchButton = (props) => (
  <CreateButton
    {...props}
    variant="search"
    icon={<SearchIcon fontSize="small" />}
  />
);

export const CopyButton = (props) => (
  <CreateButton
    {...props}
    variant="copy"
    icon={<ContentCopyIcon fontSize="small" />}
  />
);

export const CancelButton = (props) => (
  <CreateButton
    {...props}
    variant="cancel"
    // icon={<CancelIcon fontSize="small" />}
  />
);

export const UploadButton = ({
  label = "Upload",
  onChange,
  outline = false,
  fileUrl = "", // Pass the uploaded file URL here
  className = "",
  ...props
}) => {
  const baseClasses =
    "btn font-sans text-md font-medium py-2 px-3 rounded inline-flex items-center justify-center gap-2 cursor-pointer";
  const colorClasses = outline
    ? "text-indigo-600 border border-indigo-600 hover:bg-gray-100"
    : "text-white bg-indigo-600 hover:bg-indigo-700";
  const finalClass = `${baseClasses} ${colorClasses} ${className}`;

  return (
    <div className="flex items-center gap-3">
      {fileUrl && (
        <a
          href={fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-blue-600 underline"
        >
          View
        </a>
      )}

      <label className={finalClass}>
        <CloudUploadIcon fontSize="small" />
        {label}
        <input type="file" onChange={onChange} className="hidden" {...props} />
      </label>
    </div>
  );
};

export const ModalButton = ({
  buttonLabel = "Open Modal",
  modalTitle = "Modal Title",
  children,
}) => {
  const [open, setOpen] = useState(false);

  const handleToggle = () => setOpen(!open);

  return (
    <>
      <button
        type="button"
        onClick={handleToggle}
        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
      >
        {buttonLabel}
      </button>

      <Dialog open={open} onClose={handleToggle} maxWidth="sm" fullWidth>
        <DialogTitle className="flex justify-between items-center">
          <span className="text-lg font-semibold">{modalTitle}</span>
          <IconButton onClick={handleToggle} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers className="space-y-4">
          {children}
        </DialogContent>
      </Dialog>
    </>
  );
};